import { allSteps, phaseStepCounts, processBranches, processPhases } from './project'
import type { CanvasLayerVisibility, ProjectRow, StepStatus, TruthState } from '../types'

export type CanvasScope = 'general' | 'project'
export type CanvasMode = 'plan-2d' | 'layers-3d' | 'pov-3d'
export type CanvasSceneMode = Exclude<CanvasMode, 'plan-2d'>
export type CanvasLayerKey = 'reference' | 'project'
export type CanvasPhaseTiming = 'reference' | 'past' | 'current' | 'future'

export type CanvasStepProjection = {
  id: string
  name: string
  phaseId: string
  parentStepId?: string
  phaseName: string
  phaseOrder: number
  order: number
  isSubstep: boolean
  current: boolean
  optional: boolean
  status?: StepStatus
  truthState: TruthState
  expectedOutput?: string
  nextAction?: string
  note?: string
  dates: {
    baselineStart?: string
    baselineEnd?: string
    forecastStart?: string
    forecastEnd?: string
    actualStart?: string
    completed?: string
    statusSince?: string
  }
}

export type CanvasPhaseProjection = {
  id: string
  name: string
  order: number
  timing: CanvasPhaseTiming
  current: boolean
  stepCount: number
  primaryStepCount: number
  steps: CanvasStepProjection[]
}

export type CanvasBranchProjection = {
  id: string
  name: string
  trigger: string
  truthState: TruthState
  applicability: 'reference' | 'oui' | 'non' | 'a_confirmer'
  steps: Array<{ id: string; name: string; order: number; truthState: TruthState }>
}

export type CanvasProjection = {
  scope: CanvasScope
  entity: {
    id: string
    code: string
    name: string
    version: number
    referenceId: string
    asOfDate: string
    fictitious: boolean
  }
  currentPhaseId?: string
  currentStepId?: string
  phases: CanvasPhaseProjection[]
  branches: CanvasBranchProjection[]
}

/**
 * A Couches view starts from one intelligible source of truth.
 * The comparison layer is always an explicit choice, never visual noise.
 */
export function defaultCanvasLayerVisibility(scope: CanvasScope): CanvasLayerVisibility {
  return scope === 'project'
    ? { reference: false, project: true }
    : { reference: true, project: false }
}

export function buildCanvasProjection(project: ProjectRow, scope: CanvasScope): CanvasProjection {
  const phaseSource = scope === 'general'
    ? project.snapshot.reference.phases.filter((phase) => phase.enabled !== false).sort((left, right) => left.order - right.order)
    : processPhases(project.snapshot)
  const flatProjectSteps = scope === 'project' ? allSteps(project.snapshot) : []
  const flatById = new Map(flatProjectSteps.map((step) => [step.id, step]))
  const currentPhaseOrder = scope === 'project'
    ? phaseSource.find((phase) => phase.id === project.current_phase_id)?.order
    : undefined

  const phases: CanvasPhaseProjection[] = phaseSource.map((phase) => {
    const counts = phaseStepCounts(phase)
    const steps: CanvasStepProjection[] = []
    let sequence = 0

    for (const step of phase.steps.filter((item) => item.enabled !== false)) {
      sequence += 1
      const flat = flatById.get(step.id)
      steps.push({
        id: step.id,
        name: step.name,
        phaseId: phase.id,
        parentStepId: undefined,
        phaseName: phase.name,
        phaseOrder: phase.order,
        order: step.order ?? sequence,
        isSubstep: false,
        current: scope === 'project' && step.id === project.current_step_id,
        optional: Boolean(step.optional),
        status: flat?.state.status,
        truthState: flat?.state.truth_state ?? step.truth_state,
        expectedOutput: step.expected_output,
        nextAction: flat?.state.next_action,
        note: flat?.state.note ?? step.note,
        dates: {
          baselineStart: flat?.state.baseline_start_date,
          baselineEnd: flat?.state.baseline_end_date,
          forecastStart: flat?.state.forecast_start_date,
          forecastEnd: flat?.state.forecast_end_date,
          actualStart: flat?.state.actual_start_date,
          completed: flat?.state.completed_date,
          statusSince: flat?.state.status_since,
        },
      })

      for (const [substepIndex, substep] of (step.substeps ?? []).entries()) {
        const flatSubstep = flatById.get(substep.id)
        steps.push({
          id: substep.id,
          name: substep.name,
          phaseId: phase.id,
          parentStepId: step.id,
          phaseName: phase.name,
          phaseOrder: phase.order,
          order: (step.order ?? sequence) + (substepIndex + 1) / 100,
          isSubstep: true,
          current: scope === 'project' && substep.id === project.current_step_id,
          optional: Boolean(step.optional),
          status: flatSubstep?.state.status,
          truthState: flatSubstep?.state.truth_state ?? substep.truth_state ?? step.truth_state,
          expectedOutput: step.expected_output,
          nextAction: flatSubstep?.state.next_action,
          note: flatSubstep?.state.note,
          dates: {
            baselineStart: flatSubstep?.state.baseline_start_date,
            baselineEnd: flatSubstep?.state.baseline_end_date,
            forecastStart: flatSubstep?.state.forecast_start_date,
            forecastEnd: flatSubstep?.state.forecast_end_date,
            actualStart: flatSubstep?.state.actual_start_date,
            completed: flatSubstep?.state.completed_date,
            statusSince: flatSubstep?.state.status_since,
          },
        })
      }
    }

    const current = scope === 'project' && phase.id === project.current_phase_id
    const timing: CanvasPhaseTiming = scope === 'general' || currentPhaseOrder === undefined
      ? 'reference'
      : phase.order < currentPhaseOrder
        ? 'past'
        : current
          ? 'current'
          : 'future'

    return {
      id: phase.id,
      name: phase.name,
      order: phase.order,
      timing,
      current,
      stepCount: counts.total,
      primaryStepCount: counts.primary,
      steps: steps.sort((left, right) => left.order - right.order),
    }
  })

  const branches: CanvasBranchProjection[] = processBranches(project.snapshot)
    .filter((branch) => branch.enabled !== false)
    .map((branch) => {
      const sourceSteps = branch.steps?.filter((step) => step.enabled !== false)
      const labels = sourceSteps?.length ? sourceSteps.map((step) => step.name) : branch.steps_summary
      return {
        id: branch.id,
        name: branch.name,
        trigger: branch.trigger,
        truthState: branch.truth_state,
        applicability: scope === 'general' ? 'reference' : project.snapshot.project.derogation_applicability,
        steps: labels.map((name, index) => ({
          id: sourceSteps?.[index]?.id ?? `${branch.id}-${index + 1}`,
          name,
          order: index + 1,
          truthState: sourceSteps?.[index]?.truth_state ?? branch.truth_state,
        })),
      }
    })

  return {
    scope,
    entity: {
      id: scope === 'general' ? `reference:${project.snapshot.reference.id}` : project.id,
      code: scope === 'general' ? project.snapshot.reference.id : project.code,
      name: scope === 'general' ? 'Référence générale' : project.name,
      version: project.version,
      referenceId: project.snapshot.reference.id,
      asOfDate: scope === 'general' ? project.snapshot.reference.effective_date : project.snapshot.as_of_date,
      fictitious: scope === 'project' && project.snapshot.project.truth_state === 'prototype_fictif',
    },
    currentPhaseId: scope === 'project' ? project.current_phase_id : undefined,
    currentStepId: scope === 'project' ? project.current_step_id : undefined,
    phases,
    branches,
  }
}

export function defaultCanvasPhaseId(projection: CanvasProjection, mode: CanvasSceneMode) {
  if (projection.currentPhaseId && projection.phases.some((phase) => phase.id === projection.currentPhaseId)) {
    return projection.currentPhaseId
  }
  if (mode === 'pov-3d') {
    return projection.phases[Math.floor((projection.phases.length - 1) / 2)]?.id
  }
  return projection.phases[0]?.id
}

export function canvasProjectionSignature(projection: CanvasProjection) {
  return {
    scope: projection.scope,
    entity: projection.entity,
    currentPhaseId: projection.currentPhaseId,
    currentStepId: projection.currentStepId,
    phases: projection.phases.map((phase) => ({
      id: phase.id,
      name: phase.name,
      order: phase.order,
      timing: phase.timing,
      steps: phase.steps.map((step) => ({
        id: step.id,
        status: step.status,
        truthState: step.truthState,
        dates: step.dates,
      })),
    })),
    branches: projection.branches.map((branch) => ({
      id: branch.id,
      truthState: branch.truthState,
      applicability: branch.applicability,
      steps: branch.steps.map((step) => step.id),
    })),
  }
}
